/**
 * The banner a tool raises with `reportSessionExpired()` after a 401.
 *
 * „Erneut anmelden“ signs in again in a popup: the page, its edits and its
 * running jobs stay, and the paused polls go on. Signing out — the only way
 * back before — reloads the page and loses all of it, so it is the fallback.
 * Inside the Media Manager „Erneut anmelden“ asks it for a new sign-in, and
 * signing out is the Media Manager's.
 */

import { LogIn, LogOut, TriangleAlert } from 'lucide-react'
import { useState, useSyncExternalStore } from 'react'

import { useAuth } from '../../hooks/useAuth'
import { clearSessionExpired, isSessionExpired, subscribeSession } from '../../utils/session'
import { toast } from '../../utils/toast'
import Button from '../ui/Button'

const POPUP_FAILED =
    'Die Anmeldung im Fenster hat nicht geklappt. Bitte Pop-ups für diese Seite erlauben oder abmelden und neu anmelden.'
const HOST_FAILED = 'Die Anmeldung aus dem Media Manager ist nicht angekommen. Bitte den Media Manager neu laden.'

export default function SessionExpiredBanner() {
    const expired = useSyncExternalStore(subscribeSession, isSessionExpired, isSessionExpired)
    const { logout, reauthenticate, hostSignIn } = useAuth()
    const [signingIn, setSigningIn] = useState(false)
    if (!expired) return null

    const signInAgain = async () => {
        setSigningIn(true)
        try {
            if (await reauthenticate()) clearSessionExpired()
            else toast.error(hostSignIn ? HOST_FAILED : POPUP_FAILED)
        } finally {
            setSigningIn(false)
        }
    }

    return (
        <div
            role="alert"
            className="flex shrink-0 items-center justify-between gap-4 border-b border-danger/30 bg-danger-soft px-6 py-3"
        >
            <div className="flex items-center gap-3">
                <TriangleAlert className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
                <div>
                    <p className="text-sm font-semibold text-fg-strong">Anmeldung abgelaufen</p>
                    <p className="text-sm text-fg">
                        Bitte erneut anmelden – die Seite und laufende Aufträge bleiben erhalten.
                    </p>
                </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                <Button icon={LogIn} loading={signingIn} onClick={() => void signInAgain()}>
                    Erneut anmelden
                </Button>
                {!hostSignIn && (
                    <Button
                        variant="ghost"
                        icon={LogOut}
                        disabled={signingIn}
                        onClick={() => {
                            clearSessionExpired()
                            logout()
                        }}
                    >
                        Abmelden
                    </Button>
                )}
            </div>
        </div>
    )
}
